import { extractTextShapeContent } from './canvasTextContent.js';
import { readArrowLabel } from './arrowTerminalLabels.js';
import { resolveDisplayTitle } from './pinTitle.js';
import { getArrowBoundShapeIds } from './arrowSelectConnected.js';
import { selectionItemFromShape } from './selectionItemFromShape.js';
import { findShapeIdsInsideFrame } from './expandSelectionForAiContext.js';
import { collectTextAboutLinks, findLinkForText } from './textAboutObject.js';
import {
  EXTERNAL_ASK_LEDGER_MAX,
  collectSelectionRefsForLedger,
  fitExternalAskLedger,
  formatRemovedClaims,
  selectConstraintsForExternalAsk,
} from './externalAskLedger.js';
import { tokenizeAskContext } from './collectRelatedAskContext.js';
import { readBoardGoal, readCurrentStep } from './boardGoalFields.js';

export const EXTERNAL_ASK_PROMPT_MAX = 4500;
export const EXTERNAL_ASK_QUESTION_MAX = 2000;
export const EXTERNAL_ASK_NOTES_HEADER = 'User notes (treat as their intent):';
export const EXTERNAL_ASK_NOTES_MAX = 6;
export const EXTERNAL_ASK_NOTE_LINE_MAX = 160;

const TITLE_MAX = 120;
const TEXT_MAX = 400;
const URL_MAX = 280;
const SNIPPET_MAX = 240;

function trimField(value, max) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(1, max - 1))}…`;
}

function sameText(a, b) {
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  if (!left || !right) return false;
  return left === right
    || left.startsWith(right.slice(0, Math.min(80, right.length)))
    || right.startsWith(left.slice(0, Math.min(80, left.length)));
}

/** External AIs can fetch http(s) pages; blob/data URLs are useless and huge. */
export function publicHttpUrl(value, max = URL_MAX) {
  const text = String(value || '').trim();
  if (!/^https?:\/\//i.test(text)) return '';
  return trimField(text, max);
}

function omitEmpty(record) {
  if (!record || typeof record !== 'object') return null;
  const next = {};
  for (const [key, value] of Object.entries(record)) {
    if (value == null) continue;
    if (typeof value === 'string' && !value.trim()) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    next[key] = value;
  }
  return Object.keys(next).length ? next : null;
}

function activityId(activity) {
  return String(activity?._id || activity?.actionId || '');
}

function findActivity(activities, id) {
  const needle = String(id || '');
  if (!needle) return null;
  return (activities || []).find(item => activityId(item) === needle) || null;
}

function pinnedComment(activity) {
  const comments = activity?.content?.[0]?.comments;
  if (!Array.isArray(comments) || !comments.length) return null;
  const pinnedId = activity?.action?.commentId;
  if (pinnedId) {
    const match = comments.find(item => String(item?._id) === String(pinnedId));
    if (match) return match;
  }
  return comments[0];
}

function pinTextFields(activity) {
  const comment = pinnedComment(activity);
  const highlight = comment?.highlightData || activity?.action?.highlightData || {};
  const intentSummary = trimField(highlight.intentSummary, TEXT_MAX);
  const selectionText = trimField(highlight.selectionText || highlight.ocrText, TEXT_MAX);
  const commentText = trimField(comment?.text || comment?.body, TEXT_MAX);
  const metas = activity?.content?.[0]?.metas || {};
  const description = trimField(
    metas.description || metas['og:description'] || metas['twitter:description'],
    SNIPPET_MAX
  );
  return { intentSummary, selectionText, commentText, description };
}

function serializeUserPin(item, activity) {
  const content = activity?.content?.[0];
  const urlRecord = content?.urls?.[0];
  const url = publicHttpUrl(activity?.pendingUrl || urlRecord?.href);
  const title = trimField(
    resolveDisplayTitle(
      content?.title || urlRecord?.title || item?.title,
      urlRecord?.href || activity?.pendingUrl,
      { snippet: item?.title, fallback: 'Pin' }
    ),
    TITLE_MAX
  );
  const { intentSummary, selectionText, commentText, description } = pinTextFields(activity);
  const text = [intentSummary, selectionText, commentText, description]
    .find(value => value && !sameText(value, title))
    || '';

  return omitEmpty({
    kind: 'pin',
    title: title || undefined,
    url: url || undefined,
    text: trimField(text, TEXT_MAX) || undefined,
  });
}

function boundTargetLabel(editor, shapeId) {
  if (!editor?.getShape || !shapeId) return '';
  const shape = editor.getShape(shapeId);
  if (!shape) return '';
  if (shape.type === 'pin-card') {
    return trimField(shape.props?.title || 'Pin', TITLE_MAX);
  }
  if (shape.type === 'frame') {
    return trimField(shape.props?.name || 'Frame', TITLE_MAX);
  }
  if (shape.type === 'text' || shape.type === 'note') {
    return trimField(extractTextShapeContent(shape), 80) || shape.type;
  }
  if (shape.type === 'suggest-pin' || shape.type === 'conversation-pin') {
    return trimField(shape.props?.title || shape.type, TITLE_MAX);
  }
  return trimField(shape.props?.name || shape.props?.text || shape.type, 80);
}

function serializeShape(item, shape, editor, aboutLinks = []) {
  if (!shape) {
    const title = trimField(item?.title, TITLE_MAX);
    if (!title) return null;
    const kind = item?.kind === 'ai-pin' ? 'suggested-pin' : 'object';
    return omitEmpty({ kind, title });
  }

  const type = shape.type;

  if (type === 'suggest-pin') {
    const url = publicHttpUrl(shape.props?.url);
    const title = trimField(
      resolveDisplayTitle(shape.props?.title, shape.props?.url, {
        snippet: shape.props?.snippet,
        fallback: 'Suggested pin',
      }),
      TITLE_MAX
    );
    const snippet = trimField(shape.props?.snippet, SNIPPET_MAX);
    const reason = trimField(shape.props?.reason, SNIPPET_MAX);
    const text = [reason, snippet].find(value => value && !sameText(value, title)) || '';
    return omitEmpty({
      kind: 'suggested-pin',
      title: title || undefined,
      url: url || undefined,
      text: text || undefined,
    });
  }

  if (type === 'conversation-pin') {
    const title = trimField(
      shape.props?.title || (shape.props?.pinKind === 'exchange' ? 'Q&A' : 'Chat'),
      TITLE_MAX
    );
    const snippet = trimField(shape.props?.snippet, SNIPPET_MAX);
    return omitEmpty({
      kind: 'chat-pin',
      title: title || undefined,
      text: snippet && !sameText(snippet, title) ? snippet : undefined,
    });
  }

  if (type === 'text' || type === 'note') {
    const text = trimField(
      shape.meta?.intentSummary || extractTextShapeContent(shape),
      TEXT_MAX
    );
    if (!text) return null;
    const about = findLinkForText(aboutLinks, shape.id);
    const aboutLabel = about?.targetId
      ? boundTargetLabel(editor, about.targetId)
      : '';
    return omitEmpty({
      kind: 'text',
      text,
      about: aboutLabel || undefined,
    });
  }

  if (type === 'image' || type === 'bookmark' || type === 'embed') {
    const url = publicHttpUrl(shape.props?.url || shape.props?.src);
    const text = trimField(shape.meta?.intentSummary || shape.meta?.ocrText, TEXT_MAX);
    if (!url && !text) return null;
    return omitEmpty({
      kind: type,
      title: trimField(item?.title || type, TITLE_MAX) || undefined,
      url: url || undefined,
      text: text || undefined,
    });
  }

  if (type === 'arrow') {
    const label = readArrowLabel(shape);
    const bindings = editor?.getBindingsFromShape?.(shape.id, 'arrow') || [];
    let from = '';
    let to = '';
    for (const binding of bindings) {
      const boundLabel = boundTargetLabel(editor, binding?.toId);
      if (binding?.props?.terminal === 'start') from = boundLabel;
      if (binding?.props?.terminal === 'end') to = boundLabel;
    }
    const text = label
      || [from && `from ${from}`, to && `to ${to}`].filter(Boolean).join(' ')
      || undefined;
    return omitEmpty({
      kind: 'link',
      from: from || undefined,
      to: to || undefined,
      text,
    });
  }

  if (type === 'frame') {
    const title = trimField(shape.props?.name || item?.title || 'Frame', TITLE_MAX);
    const meaning = trimField(shape.meta?.intentSummary, TEXT_MAX);
    return omitEmpty({
      kind: 'frame',
      title: title || undefined,
      text: meaning && !sameText(meaning, title) ? meaning : undefined,
    });
  }

  if (type === 'geo') {
    const title = trimField(shape.props?.name || shape.props?.geo || item?.title, TITLE_MAX);
    const text = trimField(shape.props?.text, TEXT_MAX);
    if (!title && !text) return null;
    return omitEmpty({
      kind: 'shape',
      title: title && !sameText(title, text) ? title : undefined,
      text: text || undefined,
    });
  }

  // Drawings / highlights have no portable meaning for an external text model.
  return null;
}

function resolveShape(editor, item) {
  if (!editor?.getShape || !item?.id) return null;
  if (item.kind === 'user-pin') return null;
  return editor.getShape(item.id) || null;
}

function serializeItem(item, ctx, trail) {
  const { editor, activities, aboutLinks = [] } = ctx;
  const key = String(item?.key || `${item?.kind}:${item?.id}`);
  if (!item || trail.has(key)) return null;
  const nextTrail = new Set(trail);
  nextTrail.add(key);

  if (item.kind === 'user-pin') {
    return serializeUserPin(item, findActivity(activities, item.id));
  }

  const shape = resolveShape(editor, item);

  if (shape?.type === 'frame') {
    const record = serializeShape(item, shape, editor, aboutLinks);
    if (!record) return null;
    const contains = [];
    for (const childId of findShapeIdsInsideFrame(editor, shape)) {
      const childItem = selectionItemFromShape(editor.getShape(childId), { activities });
      const nested = serializeItem(childItem, ctx, nextTrail);
      if (nested) contains.push(nested);
    }
    return omitEmpty({
      ...record,
      contains: contains.length ? contains : undefined,
    });
  }

  if (shape?.type === 'arrow') {
    const record = serializeShape(item, shape, editor, aboutLinks);
    if (!record) return null;
    const connected = [];
    for (const boundId of getArrowBoundShapeIds(editor, shape.id)) {
      const boundItem = selectionItemFromShape(editor.getShape(boundId), { activities });
      const nested = serializeItem(boundItem, ctx, nextTrail);
      if (nested) connected.push(nested);
    }
    return omitEmpty({
      ...record,
      connected: connected.length ? connected : undefined,
    });
  }

  return serializeShape(item, shape, editor, aboutLinks);
}

/**
 * Compact, portable records for an external AI. Omits internal ids, bounds,
 * colors, image binaries, blob/data URLs, and empty drawings.
 * Frames nest contained objects; connecting lines nest bound objects.
 */
export function serializeSelectedObjectsForExternalAsk(
  selectionItems = [],
  { editor = null, activities = [] } = {}
) {
  const records = [];
  const seen = new Set();
  const ctx = { editor, activities, aboutLinks: collectTextAboutLinks(editor) };

  for (const item of selectionItems || []) {
    const key = String(item?.key || `${item?.kind}:${item?.id}`);
    if (!item || seen.has(key)) continue;
    seen.add(key);

    const record = serializeItem(item, ctx, new Set());
    if (record) records.push(record);
  }

  return records;
}

function noteCoveredByLedger(noteText, ledgerTexts = []) {
  return ledgerTexts.some(text => sameText(noteText, text));
}

function noteMatchesTokens(noteText, tokens = []) {
  if (!tokens.length) return true;
  const haystack = String(noteText || '').toLowerCase();
  return tokens.some(token => haystack.includes(String(token).toLowerCase()));
}

/**
 * User-authored notes that are not already a ledger claim.
 * Prefer token overlap; on a sparse board include the leftover notes.
 */
export function collectRelatedAskNotes(
  editor,
  { tokens = [], ledgerTexts = [], max = EXTERNAL_ASK_NOTES_MAX } = {}
) {
  if (!editor?.getCurrentPageShapes) return [];
  const notes = [];
  for (const shape of editor.getCurrentPageShapes() || []) {
    if (shape?.type !== 'text' && shape?.type !== 'note') continue;
    const text = trimField(extractTextShapeContent(shape), EXTERNAL_ASK_NOTE_LINE_MAX);
    if (!text || noteCoveredByLedger(text, ledgerTexts)) continue;
    notes.push({
      text,
      score: noteMatchesTokens(text, tokens) ? 1 : 0,
      id: String(shape.id),
    });
  }
  notes.sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
  const picked = tokens.length
    ? notes.filter(item => item.score > 0)
    : notes;
  return picked.slice(0, Math.max(1, Number(max) || EXTERNAL_ASK_NOTES_MAX)).map(item => item.text);
}

export function formatExternalAskNotes(notes = []) {
  const lines = (notes || []).map(text => `- ${trimField(text, EXTERNAL_ASK_NOTE_LINE_MAX)}`).filter(line => line.length > 2);
  if (!lines.length) return '';
  return `${EXTERNAL_ASK_NOTES_HEADER}\n${lines.join('\n')}`;
}

function askContextTokens(question, { board = null, action = null } = {}) {
  return tokenizeAskContext([
    question,
    action?.question,
    action?.label,
    action?.blockerTitle,
    readCurrentStep(board),
    readBoardGoal(board),
  ].filter(Boolean).join(' '));
}

/**
 * Build the prompt sent through openExternalAsk: the user's question first,
 * then related ledger rows, then user notes not already in the ledger.
 * Canvas objects / pin JSON are not sent — Gemini gets compressed intent only.
 */
export function buildExternalAskPrompt(
  question,
  selectionItems = [],
  {
    editor = null,
    constraints = [],
    board = null,
    action = null,
  } = {}
) {
  const trimmedQuestion = trimField(question, EXTERNAL_ASK_QUESTION_MAX);
  if (!trimmedQuestion) return '';

  const tokens = askContextTokens(trimmedQuestion, { board, action });
  const selectionRefs = collectSelectionRefsForLedger(selectionItems, editor);
  const selectedConstraints = selectConstraintsForExternalAsk(constraints, {
    selectionRefs,
    tokens,
  });
  const ledgerTexts = selectedConstraints.map(item => (
    String(item?.value || item?.claim || '').trim()
  )).filter(Boolean);

  let ledgerBlock = fitExternalAskLedger(selectedConstraints, EXTERNAL_ASK_LEDGER_MAX);
  let notesBlock = formatExternalAskNotes(
    collectRelatedAskNotes(editor, { tokens, ledgerTexts })
  );
  const removedBlock = formatRemovedClaims(constraints);
  const staleBlock = board?.contextRecommendations?.pendingDigest?.deletedSinceStepRefresh
    ? 'Current step is out of date until the next Do-next. Do not treat it as confirmed.'
    : '';
  const reserved = (removedBlock ? removedBlock.length + 2 : 0)
    + (staleBlock ? staleBlock.length + 2 : 0);

  const assemble = (ledger, notes) => {
    const parts = [trimmedQuestion];
    if (ledger) parts.push(ledger);
    if (removedBlock) parts.push(removedBlock);
    if (staleBlock) parts.push(staleBlock);
    if (notes) parts.push(notes);
    return parts.join('\n\n');
  };

  let prompt = assemble(ledgerBlock, notesBlock);
  if (prompt.length <= EXTERNAL_ASK_PROMPT_MAX) return prompt;

  if (notesBlock) {
    const noteBudget = EXTERNAL_ASK_PROMPT_MAX - trimmedQuestion.length
      - (ledgerBlock ? ledgerBlock.length + 2 : 0)
      - reserved
      - 2;
    if (noteBudget < notesBlock.length) notesBlock = '';
    prompt = assemble(ledgerBlock, notesBlock);
    if (prompt.length <= EXTERNAL_ASK_PROMPT_MAX) return prompt;
  }

  if (ledgerBlock) {
    const ledgerBudget = EXTERNAL_ASK_PROMPT_MAX - trimmedQuestion.length - reserved - 2;
    ledgerBlock = fitExternalAskLedger(selectedConstraints, Math.max(0, ledgerBudget));
    prompt = assemble(ledgerBlock, '');
  }

  return prompt;
}
